import React from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AnDatePicker from '../../components/DateTime/DatePicker'
import { FormSection } from './FormKit'
import { describeGap, employmentGaps } from './applicationHistory'

const monthPickerProps = {
  views: ['year', 'month'],
  openTo: 'year',
  format: 'MMM YYYY',
  disableFuture: true,
  size: 'small',
  fullWidth: true,
}

const JobCard = ({ job, index, onChange, onRemove }) => {
  const set = (field) => (e) => onChange({ [field]: e.target.value })
  return (
    <FormSection
      title={job.employerName || `Job ${index + 1}`}
      caption={job.jobTitle}
      action={
        <Tooltip title="Remove this job">
          <IconButton onClick={onRemove} edge="end">
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Employer"
            size="small"
            fullWidth
            required
            value={job.employerName ?? ''}
            onChange={set('employerName')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Job title"
            size="small"
            fullWidth
            required
            value={job.jobTitle ?? ''}
            onChange={set('jobTitle')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AnDatePicker
            label="Start date"
            required
            date={job.startDate ?? null}
            onChange={(date) => onChange({ startDate: date })}
            {...monthPickerProps}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          {job.stillPresent ? (
            <Box
              height={40}
              display="flex"
              alignItems="center"
              px={1.5}
              borderRadius={1}
              bgcolor="anMuted.main"
            >
              <Typography variant="body2">Current job</Typography>
            </Box>
          ) : (
            <AnDatePicker
              label="End date"
              required
              date={job.endDate ?? null}
              onChange={(date) => onChange({ endDate: date })}
              {...monthPickerProps}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={!!job.stillPresent}
                onChange={(e) =>
                  onChange({
                    stillPresent: e.target.checked,
                    ...(e.target.checked && { endDate: null }),
                  })
                }
              />
            }
            label="I currently work here"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Employer address"
            size="small"
            fullWidth
            value={job.employerAddress?.line1 ?? ''}
            onChange={(e) =>
              onChange({
                employerAddress: {
                  ...job.employerAddress,
                  line1: e.target.value,
                },
              })
            }
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Town"
            size="small"
            fullWidth
            value={job.employerAddress?.town ?? ''}
            onChange={(e) =>
              onChange({
                employerAddress: { ...job.employerAddress, town: e.target.value },
              })
            }
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Postcode"
            size="small"
            fullWidth
            value={job.employerAddress?.postCode ?? ''}
            onChange={(e) =>
              onChange({
                employerAddress: {
                  ...job.employerAddress,
                  postCode: e.target.value.toUpperCase(),
                },
              })
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Employer phone"
            size="small"
            type="tel"
            fullWidth
            value={job.employerPhone ?? ''}
            onChange={set('employerPhone')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Employer email"
            size="small"
            type="email"
            fullWidth
            value={job.email ?? ''}
            onChange={set('email')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Main duties and responsibilities"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={job.responsibilities ?? ''}
            onChange={set('responsibilities')}
          />
        </Grid>
        {!job.stillPresent && (
          <Grid item xs={12}>
            <TextField
              label="Reason for leaving"
              size="small"
              fullWidth
              value={job.reasonForLeaving ?? ''}
              onChange={set('reasonForLeaving')}
            />
          </Grid>
        )}
      </Grid>
    </FormSection>
  )
}

const EmploymentHistory = ({ formValues, setFormValues }) => {
  const jobs = formValues?.workExperience ?? []
  const noHistory = !!formValues?.noEmploymentHistory
  const gaps = employmentGaps(jobs)

  const setJobs = (updater) =>
    setFormValues((prev) => ({
      ...prev,
      workExperience: updater(prev.workExperience ?? []),
    }))

  return (
    <Stack gap={2}>
      <Alert severity="info" icon={false}>
        As a CQC-registered provider we need your full employment history,
        including any voluntary work, with a written explanation of any gaps.
        Start with your current or most recent job.
      </Alert>

      {!noHistory &&
        jobs.map((job, index) => (
          <JobCard
            key={job._id ?? index}
            job={job}
            index={index}
            onChange={(patch) =>
              setJobs((list) =>
                list.map((entry, i) =>
                  i === index ? { ...entry, ...patch } : entry,
                ),
              )
            }
            onRemove={() =>
              setJobs((list) => list.filter((_, i) => i !== index))
            }
          />
        ))}

      {!noHistory && (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setJobs((list) => [...list, { stillPresent: false }])}
          sx={{ alignSelf: 'flex-start', bgcolor: 'background.paper' }}
        >
          {jobs.length ? 'Add another job' : 'Add a job'}
        </Button>
      )}

      {!noHistory && gaps.length > 0 && (
        <FormSection title="Gaps in employment">
          <Alert severity="warning">
            <AlertTitle>
              We found {gaps.length === 1 ? 'a gap' : `${gaps.length} gaps`} of
              more than a month
            </AlertTitle>
            {gaps.map((gap) => (
              <Box key={gap.from.toISOString()}>{describeGap(gap)}</Box>
            ))}
          </Alert>
          <TextField
            label="What were you doing during these periods?"
            helperText="For example study, caring responsibilities, travel or looking for work."
            required
            fullWidth
            multiline
            minRows={3}
            value={formValues?.employmentGapsExplanation ?? ''}
            onChange={(e) =>
              setFormValues((prev) => ({
                ...prev,
                employmentGapsExplanation: e.target.value,
              }))
            }
          />
        </FormSection>
      )}

      {(noHistory || jobs.length === 0) && (
        <FormSection>
          <FormControlLabel
            control={
              <Checkbox
                checked={noHistory}
                onChange={(e) =>
                  setFormValues((prev) => ({
                    ...prev,
                    noEmploymentHistory: e.target.checked,
                  }))
                }
              />
            }
            label="I have not been employed before (including voluntary work)"
          />
        </FormSection>
      )}
    </Stack>
  )
}

export default EmploymentHistory
